import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  signal,
  ViewChild,
  WritableSignal,
} from '@angular/core';
import { SliderComponent } from '@appkit4/angular-components/slider';
import { SliderOptions } from './slider-options.interface';
import { DefaultFilterComponent } from '../base-components/default-filter.component';

@Component({
  standalone: false,
  selector: 'gstar-slider-filter',
  templateUrl: './slider-filter.component.html',
  styleUrl: './slider-filter.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SliderFilterComponent
  extends DefaultFilterComponent<number[]>
  implements OnInit
{
  range: WritableSignal<number[]> = signal([]);
  minValue = 0;
  maxValue = 100;
  steps = 1;
  readonly wholeNumberPattern: RegExp = /^\d+$/;

  @ViewChild('slider') slider!: SliderComponent;

  constructor() {
    super();

    this.clearAll.subscribe(() => {
      this.range.set([this.minValue, this.maxValue]);
    });

    this.checkStatus.subscribe(() => {
      if (this.isDisabled()) {
        this.selectedFilter().reset();
        this.range.set([this.minValue, this.maxValue]);
        this.resetAppkitSlider();
      }
    });
  }

  override ngOnInit(): void {
    super.ngOnInit();
    if (this.value()) {
      this.range.set(this.value() as number[]);
    } else {
      const range = this.filterColumn().getOptions<SliderOptions>();
      this.range.set([range.min, range.max]);
    }
  }

  startChangeHandler(e: Event): void {
    if (e.target) {
      const target = e.target as HTMLInputElement;
      if (target.value) {
        const isWholeNumber = this.isWholeNumber(target.value);
        const validatedValue = isWholeNumber
          ? parseInt(target.value, 10)
          : this.range()[0];
        this.range.set([validatedValue, this.range()[1]]);
      }
    }
  }

  endChangeHandler(e: Event): void {
    if (e.target) {
      const target = e.target as HTMLInputElement;

      if (target.value) {
        const isWholeNumber = this.isWholeNumber(target.value);
        const validatedValue = isWholeNumber
          ? parseInt(target.value, 10)
          : this.range()[1];
        this.range.set([this.range()[0], validatedValue]);
      }
    }
  }

  blurHandler(): void {
    const rangeValues = [...this.range()]
      .map((val: number, index: number) => {
        const checkedValue = this.hasEmptyValue(index);
        return this.ensureNumberInRange(
          checkedValue,
          this.minValue,
          this.maxValue,
          this.steps
        );
      })
      .sort((a, b) => a - b);
    this.range.set(rangeValues);
    this.updateValue();
  }

  private hasEmptyValue(index: number): number {
    if (typeof this.range()[index] !== 'number') {
      return index === 0 ? this.minValue : this.maxValue;
    }
    return this.range()[index];
  }

  private ensureNumberInRange(
    num: number,
    min: number,
    max: number,
    step: number
  ): number {
    const digits = this.countDecimals(step);
    if (digits) {
      if (((num - min) * 10 * digits) % (step * 10 * digits)) {
        num = Number(
          (
            Math.round(((num - min) * 10 * digits) / (step * 10 * digits)) *
              step +
            min
          ).toFixed(digits)
        );
      }
    } else {
      if ((num - min) % step) {
        num = Math.round((num - min) / step) * step + min;
      }
    }
    if (isNaN(num) || num < min) {
      return min;
    } else if (num > max) {
      return max;
    }
    return num;
  }

  private countDecimals(num: number): number {
    if (Math.floor(num) === num) return 0;
    return num.toString().split('.')[1].length || 0;
  }

  updateValue(): void {
    const currentValue = JSON.stringify(
      this.selectedFilter().getValue<number[]>()
    );
    if (currentValue !== JSON.stringify(this.range())) {
      this.update(this.range());
    }
  }

  private resetAppkitSlider(): void {
    this.slider.value = [this.minValue, this.maxValue];
    this.slider.updateTrackAndHandles(true);
  }

  private isWholeNumber(value: string): boolean {
    return this.wholeNumberPattern.test(value);
  }

  numberOnly(event: KeyboardEvent): boolean {
    const char = event.key;
    if (!this.wholeNumberPattern.test(char)) {
      event.preventDefault();
      return false;
    }
    return true;
  }
}
